import {
  INTERVIEW_SCHEDULE_STATUS,
  INTERVIEW_TYPE,
  InterviewStatus,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";

import { type PlacementDetail } from "../../../api/useGetPlacement";
import { getUnverifiedBookedInterview } from "../../../utils";

/**
 * Extracts and prioritizes interviews from placement details.
 * Prioritizes in-person interviews over phone screen interviews.
 */
export function usePlacementInterviews(placementDetails: PlacementDetail) {
  const { interviews } = placementDetails;

  // Include both BOOKED and PENDING status interviews (PENDING is from submit documents later flow)
  const scheduledInterviews = interviews.filter(
    (interview) =>
      interview.attributes.status === InterviewStatus.BOOKED ||
      interview.attributes.status === InterviewStatus.PENDING
  );

  const phoneScreenInterview = scheduledInterviews.find(
    (interview) => interview.attributes.type === INTERVIEW_TYPE.PHONE_SCREEN
  );

  const inPersonInterview = scheduledInterviews.find(
    (interview) => interview.attributes.type === INTERVIEW_TYPE.IN_PERSON
  );

  // Prioritize in-person interviews over phone screen interviews
  const interview = inPersonInterview ?? phoneScreenInterview ?? scheduledInterviews[0];

  // Find attended in-person interview for "talk to recruiter about next steps" flow
  const attendedInterview = interviews.find(
    (interviewItem) =>
      interviewItem.attributes.status === InterviewStatus.ATTENDANCE_CONFIRMED &&
      interviewItem.attributes.type === INTERVIEW_TYPE.IN_PERSON
  );

  // Find in-progress interview for Update ETA feature
  const inProgressInterview = interviews.find(
    (interviewItem) =>
      interviewItem.attributes.status === InterviewStatus.BOOKED &&
      interviewItem.attributes.scheduleStatus === INTERVIEW_SCHEDULE_STATUS.IN_PROGRESS
  );

  const unverifiedBookedInterview = getUnverifiedBookedInterview(placementDetails);

  return {
    scheduledInterviews,
    phoneScreenInterview,
    inPersonInterview,
    interview,
    attendedInterview,
    inProgressInterview,
    unverifiedBookedInterview,
  };
}

export type PlacementInterviewsReturn = ReturnType<typeof usePlacementInterviews>;
